import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntegrationHealthBadge } from "../components/shared/IntegrationHealth";
import { ChevronLeft, Download, Upload, Loader2, Clock, RefreshCw, CheckCircle2, XCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";

export default function ConnectorPage({ onLogout, userRole, isFeatureEnabled }) {
  const { connectorKey } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgR, histR] = await Promise.all([
        axios.get(`${API}/connectors/${connectorKey}/config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/connectors/${connectorKey}/history`, { headers: getAuthHeaders() }),
      ]);
      setConfig(cfgR.data);
      setHistory(histR.data || []);
    } catch { toast.error("Failed to load connector config"); }
    finally { setLoading(false); }
  }, [connectorKey]);

  useEffect(() => { setLoading(true); fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { meta, ...saveData } = config;
      await axios.put(`${API}/connectors/${connectorKey}/config`, saveData, { headers: getAuthHeaders() });
      toast.success(`${config.meta?.name || connectorKey} configuration saved`);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleSync = async (direction) => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/connectors/${connectorKey}/sync?direction=${direction}`, {}, { headers: getAuthHeaders() });
      toast.success(`Sync complete — ${r.data.total} record(s) synced`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  if (loading || !config) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64 text-[#64748B] font-mono">Loading...</div></Layout>;

  const meta = config.meta || {};
  const fields = meta.fields || [];
  const sensitiveFields = new Set(["api_key", "password", "bearer_token", "client_secret"]);

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid={`connector-${connectorKey}`}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate("/connectors")} className="text-xs text-[#64748B] hover:text-[#0F172A] flex items-center gap-1 mb-2">
              <ChevronLeft size={14} />All Connectors
            </button>
            <h1 className="font-heading font-black text-4xl text-[#0F172A]" data-testid="connector-title">{meta.name}</h1>
            <p className="text-[#64748B] text-sm mt-1">{meta.description}</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="connector-save-btn">
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
        </div>

        {/* Health Badge */}
        <IntegrationHealthBadge
          integration={connectorKey}
          onTestConnection={async () => {
            const r = await axios.post(`${API}/connectors/${connectorKey}/test`, {}, { headers: getAuthHeaders() });
            return r.data;
          }}
        />

        {/* Config */}
        <div className="bg-white border border-[#E2E8F0] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-sm text-[#0F172A]">Connection Settings</h3>
            <div className="flex items-center gap-3">
              <Label className="text-sm">Enabled</Label>
              <button onClick={() => update("enabled", !config.enabled)} className={`w-12 h-6 rounded-full transition-colors ${config.enabled ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} data-testid="connector-enabled-toggle">
                <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${config.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map(f => (
              <div key={f} className={f === "server_url" || f === "token_url" ? "md:col-span-2" : ""}>
                <Label className="text-sm capitalize">{f.replace(/_/g, " ")}{f === "server_url" ? " *" : ""}</Label>
                <Input
                  type={sensitiveFields.has(f) ? "password" : "text"}
                  value={config[f] || ""}
                  onChange={e => update(f, e.target.value)}
                  className="mt-1 rounded-none font-mono text-sm"
                  placeholder={f === "server_url" ? `https://${connectorKey.replace(/_/g, "-")}.example.com` : ""}
                  data-testid={`field-${f}`}
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-[#94A3B8] mt-3">Auth type: <strong>{meta.auth_type}</strong> · Base path: <code>{meta.default_base_path}</code></p>
        </div>

        {/* Sync Endpoints */}
        {meta.sync_endpoints && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3">Sync Endpoints</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.keys(meta.sync_endpoints).map(ep => (
                <span key={ep} className="px-3 py-1 bg-[#F1F5F9] border border-[#E2E8F0] text-xs font-mono text-[#334155]">{ep}</span>
              ))}
            </div>
          </div>
        )}

        {/* Sync Controls */}
        {config.enabled && (
          <div className="bg-[#0F172A] p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-bold text-xl">Manual Sync</h2>
                <p className="text-[#94A3B8] text-sm mt-1">Pull data from {meta.name} into Vault.</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handleSync("pull")} disabled={syncing} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none" data-testid="connector-pull-btn">
                  {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}Pull
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3 flex items-center gap-2"><Clock size={16} /> Sync History</h3>
            <div className="space-y-1">
              {history.slice(0, 15).map(h => (
                <div key={h.id} className="flex items-center justify-between p-2 bg-[#FAFAFA] border border-[#E2E8F0] text-sm">
                  <span className="font-mono text-xs text-[#64748B]">{new Date(h.created_at).toLocaleString()}</span>
                  <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF6FF] text-[#1E40AF] uppercase">{h.direction}</span>
                  <span className="text-xs font-bold text-[#0F172A]">{h.total} record(s)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
